import { Component, Inject, OnInit, ViewEncapsulation, ChangeDetectorRef, ViewChild } from '@angular/core';
import { MatStepper } from '@angular/material/stepper';
import { UntypedFormBuilder, UntypedFormGroup, Validators, UntypedFormArray } from '@angular/forms';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';

import { Subject } from 'rxjs';
import { Pipeline } from 'app/modules/projects/projects.types';
import {
  Account,
  ProjectType,
  PropertyType,
  ProjectStage,
  FieldDef,
  PipelineTemplate,
} from 'app/core/models/account.types';
import { AccountsService } from 'app/modules/accounts/accounts.service';
import { environment } from '@env/environment';
import * as CustomValidator from '../../../../shared/directives/custom-validators.directive';
import { EMAIL_PATTERN } from 'app/shared/Utils/common.utils';

@Component({
  selector: 'account-onboarding',
  templateUrl: './account-onboarding.component.html',
  styleUrls: ['./account-onboarding.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class AccountOnboardingComponent implements OnInit {
  accountDetailsForm: UntypedFormGroup;
  accountDetailsFormMessage: any;

  pipelineTemplateForm: UntypedFormGroup;
  pipelineTemplateFormMessage: any;

  inviteUserForm: UntypedFormGroup;
  inviteUserFormMessage: any;

  projectTypes: ProjectType[];
  propertyTypes: PropertyType[];
  projectStages: ProjectStage[];
  appDomain: string;

  private _unsubscribeAll: Subject<void>;
  public account: Account;
  public fieldDefs: FieldDef[] = [];
  public pipeline: Pipeline;
  public pipelineTemplates: PipelineTemplate[] = [];
  public selectedPipelineTemplate: PipelineTemplate;

  @ViewChild('horizontalStepper') private _stepper: MatStepper;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public matDialogRef: MatDialogRef<AccountOnboardingComponent>,
    private _formBuilder: UntypedFormBuilder,
    private _changeDetectorRef: ChangeDetectorRef,
    private _accountsService: AccountsService,
  ) {
    this._unsubscribeAll = new Subject();
  }

  ngOnInit(): void {
    this.accountDetailsForm = this._formBuilder.group({
      name: ['', [Validators.required, CustomValidator.specialCharValidator]],
      subdomain: ['', [Validators.required, CustomValidator.specialCharValidator]],
      type: ['Investor'],
    });

    this.pipelineTemplateForm = this._formBuilder.group({
      template_id: ['', [Validators.required]],
    });

    this.inviteUserForm = this._formBuilder.group({
      emails: this._formBuilder.array([]),
    });

    (this.inviteUserForm.get('emails') as UntypedFormArray).push(
      this._formBuilder.group({
        email: ['', [Validators.email, Validators.pattern(EMAIL_PATTERN)]],
      }),
    );

    this._accountsService.getPipelineTemplates().subscribe((pipelineTemplates) => {
      this.pipelineTemplates = pipelineTemplates;
    });

    this.appDomain = environment.appDomain;
  }

  /**
   * Discard the message
   */
  discard(): void {
    this.matDialogRef.close();
  }

  onPipelineTemplateChange($event: any): void {
    const template = this.pipelineTemplates.find((item) => item.id === $event.value);
    this.selectedPipelineTemplate = template;
  }

  submitAccountDetailsForm(): void {
    const accountDetails = this.accountDetailsForm.value;

    this.accountDetailsForm.disable();

    this._accountsService.createAccount(accountDetails).subscribe(
      (account) => {
        this.account = account;
        this._accountsService.getFieldDefs(+account.id).subscribe((fieldDefs) => {
          this.fieldDefs = fieldDefs;
        });
        this._stepper.next();
      },
      (response) => {
        // Re-enable the form
        this.accountDetailsForm.enable();

        // Show the error message
        this.accountDetailsFormMessage = {
          appearance: 'outline',
          content: response?.error?.user_authentication || 'Please specify a new subdomain',
          shake: true,
          showIcon: false,
          type: 'error',
        };
      },
    );
  }

  submitPipelineTemplateForm(): void {
    this.pipelineTemplateForm.disable();

    let pipeline = {
      name: 'Default Pipeline',
      template_id: this.selectedPipelineTemplate.id,
      fill_demo_data: true,
    };

    this._accountsService.createPipeline(+this.account.id, pipeline).subscribe(
      (pipeline) => {
        this.pipeline = pipeline;
        this._stepper.next();
      },
      (response) => {
        // Re-enable the form
        this.pipelineTemplateForm.enable();

        // Show the error message
        this.pipelineTemplateFormMessage = {
          appearance: 'outline',
          content: response?.error?.user_authentication || 'Please select a new template',
          shake: true,
          showIcon: false,
          type: 'error',
        };
      },
    );
  }

  submitInviteUserForm(): void {
    this.inviteUserForm.disable();

    const formVals = this.inviteUserForm.getRawValue();

    for (let invite of formVals.emails) {
      this._accountsService.sendInvitation(+this.account.id, invite).subscribe();
    }
    this.skipAndComplete();
  }

  /**
   * Remove the email field
   *
   * @param index
   */
  removeEmailField(index: number): void {
    // Get form array for emails
    const emailsFormArray = this.inviteUserForm.get('emails') as UntypedFormArray;

    // Remove the email field
    emailsFormArray.removeAt(index);
    // Mark for check
    this._changeDetectorRef.markForCheck();
  }

  /**
   * Add the email field
   */
  addEmailField(): void {
    if (this.inviteUserForm.get('emails').status === 'VALID') {
      // Create an empty email form group
      const emailFormGroup = this._formBuilder.group({
        // If user is added new email field then it required for him to pass email address.
        email: ['', [Validators.required, Validators.email, Validators.pattern(EMAIL_PATTERN)]],
      });

      // Add the email form group to the emails form array
      (this.inviteUserForm.get('emails') as UntypedFormArray).push(emailFormGroup);

      // Mark for check
      this._changeDetectorRef.markForCheck();
    }
  }

  skipAndComplete(): void {
    this.matDialogRef.close();
    window.location.href = `https://${this.account.subdomain}.${this.appDomain}/projects`;
  }

  lookupField(name) {
    let fields = this.fieldDefs.filter((fieldDef) => fieldDef.name == name);
    if (fields.length > 0) {
      return fields[0];
    } else {
      return null;
    }
  }
}
